import {Trans} from '@lingui/macro';
import {ellipsis} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {CreateFormInputDisplay} from '../../../../../components/common/createFormInputs/createFormInputDisplay';
import {AsyncLoader} from '../../../../../components/common/loaders/asyncLoader';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../components/common/skeleton';
import {useClipboard} from '../../../../../helpers/hooks/clipboardHookHelpers';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {LocalApplicationModel} from '../../../../../models/api/localApplicationModel';
import {SettingsDevelopersApplicationOAuthUrl} from '../../../../../models/url/settingsUrlModel';
import {
  fetchLocalApplicationOAuthClientAsyncAction,
  fetchLocalApplicationsOAuthClientSecretAsyncAction,
} from '../../../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../banners/settingsBannersContext';
import {DevelopersHiddenInputSkeleton} from '../../../skeletons/developersHiddenInputSkeleton';
import {DevelopersApplicationsOAuthRedirectUrls} from './developersApplicationOAuthRedirectUrls';

/*
 * Constants.
 */

const clientSecretPlaceholder = '******************************************';

/*
 * Props.
 */

interface SettingsDevelopersApplicationOAuthPageProps {
  url: SettingsDevelopersApplicationOAuthUrl;
  application: LocalApplicationModel;
}

/*
 * Style.
 */

const StyledOAuthWrapperDiv = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 0 1px 64px 1px;
  overflow: auto;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  line-height: 24px;

  &:not(:first-child) {
    margin-top: 32px;
  }
`;

const StyledAppSecretWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledAppSecretTextBox = styled(TextBox)`
  flex: 0 0 395px;
  padding-right: 4px;
  ${ellipsis()};
`;

const StyledButton = styled(Button)`
  height: 30px;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledAppSecretCreateFormInputDisplay = styled(CreateFormInputDisplay)`
  flex: 0 0 100%;
`;

const StyledTitleSkeleton = styled(Skeleton)`
  &:not(:first-child) {
    margin-top: 32px;
  }
`;

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 32px;
`;

const StyledUrlSkeleton = styled(Skeleton)`
  margin-top: 18px;

  &:first-child {
    margin-top: 24px;
  }
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationOAuthPage: FC<SettingsDevelopersApplicationOAuthPageProps> = (
  props,
) => {
  const {application} = props;
  const [shouldRevealClientSecret, setShouldRevealClientSecret] = useState(false);
  const [isFetchingSecret, setIsFetchingSecret] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const dispatch = useAppDispatch();
  const {showBanner} = useSettingsBanners();
  const {copy} = useClipboard();
  const [isUrlsValid, setIsUrlsValid] = useState(false);

  useInitialEffect(() => {
    dispatch(fetchLocalApplicationOAuthClientAsyncAction(application.id));
  });

  if (!application.oAuthClient || application.oAuthClient.status === AsyncStatusesEnum.LOADING) {
    return (
      <StyledOAuthWrapperDiv>
        <StyledTitleSkeleton height="24px" width="175px" />
        <DevelopersHiddenInputSkeleton />
        <DevelopersHiddenInputSkeleton hasRevealButton />
        <StyledTitleSkeleton height="24px" width="175px" />
        <div>
          <StyledUrlSkeleton height="30px" scheme={SkeletonColorSchemesEnum.GREY10} />
          <StyledUrlSkeleton height="30px" scheme={SkeletonColorSchemesEnum.GREY10} />
          <StyledUrlSkeleton height="30px" scheme={SkeletonColorSchemesEnum.GREY10} />
        </div>
        <StyledUrlSkeleton
          width="116px"
          height="30px"
          borderRadius="9000px"
          scheme={SkeletonColorSchemesEnum.GREY10}
        />
      </StyledOAuthWrapperDiv>
    );
  }
  if (application.oAuthClient.status === AsyncStatusesEnum.FAILED || !application.oAuthClient.value) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.FAILED}
          onRetryClick={() => dispatch(fetchLocalApplicationOAuthClientAsyncAction(application.id))}
        />
      </StyledErrorWrapperDiv>
    );
  }

  const fetchSecretAsync = async () => {
    try {
      setIsFetchingSecret(true);
      const secret = await dispatch(fetchLocalApplicationsOAuthClientSecretAsyncAction(application.id));
      setClientSecret(secret);
      return secret;
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="settings-developers-oauth-client-secret-failed-to-fetch">
            Failed to fetch client secret.
          </Trans>
        ),
      });
      return undefined;
    } finally {
      setIsFetchingSecret(false);
    }
  };

  const getAppSecretAsync = async () => {
    if (clientSecret) {
      return clientSecret;
    }
    const secret = await fetchSecretAsync();
    return secret;
  };

  const onCopyIdClickAsync = async () => {
    copy(`${application.oauthClientId}`);
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: <Trans id="settings-developers-oauth-client-id-copied">Copied client id to clipboard.</Trans>,
    });
  };

  const onCopySecretClickAsync = async () => {
    const secret = await getAppSecretAsync();
    if (!secret) {
      return;
    }

    copy(secret);
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: (
        <Trans id="settings-developers-oauth-client-secret-copied">Copied client secret to clipboard.</Trans>
      ),
    });
  };

  const onToggleReveal = () => {
    setShouldRevealClientSecret((v) => !v);
    getAppSecretAsync();
  };

  return (
    <StyledOAuthWrapperDiv>
      <StyledTitleDiv>
        <Trans id="settings-developers-oauth-app-creds-title">App credentials</Trans>
      </StyledTitleDiv>
      <StyledAppSecretCreateFormInputDisplay
        title={<Trans id="settings-developers-oauth-client-id-title">Client ID</Trans>}
        isDisabled
        input={
          <StyledAppSecretWrapperDiv>
            <StyledAppSecretTextBox value={application.oAuthClient.value.clientId} isDisabled noMargin />
            <StyledButton onClick={onCopyIdClickAsync}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name="copySmall" />
                <Trans id="settings-developers-oauth-client-id-copy">Copy</Trans>
              </StyledButtonContentWrapperDiv>
            </StyledButton>
          </StyledAppSecretWrapperDiv>
        }
      />
      <StyledAppSecretCreateFormInputDisplay
        title={<Trans id="settings-developers-oauth-client-secret-title">Client secret</Trans>}
        isDisabled
        input={
          <StyledAppSecretWrapperDiv>
            {!isFetchingSecret && (
              <StyledAppSecretTextBox
                value={shouldRevealClientSecret ? clientSecret : clientSecretPlaceholder}
                isDisabled
                noMargin
              />
            )}
            {isFetchingSecret && <Skeleton width="395px" height="30px" />}
            <StyledButton onClick={onCopySecretClickAsync} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name="copySmall" />
                <Trans id="settings-developers-oauth-client-secret-copy">Copy</Trans>
              </StyledButtonContentWrapperDiv>
            </StyledButton>
            <StyledButton onClick={onToggleReveal} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name={shouldRevealClientSecret ? 'eyeCrossedSmall' : 'eyeSmall'} />
                {!shouldRevealClientSecret && (
                  <Trans id="settings-developers-oauth-client-secret-reveal">Reveal</Trans>
                )}
                {shouldRevealClientSecret && (
                  <Trans id="settings-developers-oauth-client-secret-hide">Hide</Trans>
                )}
              </StyledButtonContentWrapperDiv>
            </StyledButton>
          </StyledAppSecretWrapperDiv>
        }
      />
      <StyledTitleDiv>
        <Trans id="settings-developers-oauth-redirect-url-title">Redirect URLs</Trans>
      </StyledTitleDiv>
      <DevelopersApplicationsOAuthRedirectUrls
        oAuthClient={application.oAuthClient.value}
        onUrlsValidationChange={setIsUrlsValid}
      />
      <br />
      <br />
      Urls Valid: {JSON.stringify(isUrlsValid)}
    </StyledOAuthWrapperDiv>
  );
};
